import { inject } from '@angular/core';
import { Leerkr8Error } from '@core/saves/leerkr8/Leerkr8Error';
import { ISavesManager, ISaveState } from '@core/saves/saves.interface';
import { waitForMillis } from '@shared/delay.utils';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { LEERKR8_API_SERVICE } from './leerkr8-api.service.interface';
import { Leerkr8Objective, Leerkr8PlaythroughRequest } from './types';

export class Leerkr8SaverService implements ISavesManager {
  private currentPlaythroughObjectives: Leerkr8Objective[] = [];

  private onLoginSubject = new Subject<string>();
  onLogin$: Observable<string> = this.onLoginSubject.asObservable();

  private sessionId?: string;
  private playerCode?: string;
  private api = inject(LEERKR8_API_SERVICE);

  // Playthrough update polling
  private pollerId?: ReturnType<typeof setInterval>; // To clear the poller interval
  private playthroughRef?: string;
  private startPollingDateString?: string;
  private startPollingTime?: number;
  private readonly POLLING_INTERVAL_MS = 10000;

  private _isLoggedIn = false;

  isLoggedIn() {
    return this._isLoggedIn;
  }

  addPlaythroughObjective(objectives: Leerkr8Objective) {
    this.currentPlaythroughObjectives.push(objectives);
  }

  async save(state: Partial<ISaveState>): Promise<void> {
    if (!this.sessionId || !this.playerCode) {
      throw new Leerkr8Error('Not logged in', 'NOT_LOGGED_IN');
    }

    const oldSave = await this.load();

    const finalState = { ...oldSave, ...state } satisfies ISaveState;

    await lastValueFrom(this.api.setGameSave(this.sessionId, this.playerCode, finalState));
  }

  async load(): Promise<ISaveState> {
    if (!this.sessionId || !this.playerCode) {
      throw new Leerkr8Error('Not logged in', 'NOT_LOGGED_IN');
    }

    return await this.api.getGameSave(this.sessionId, this.playerCode);
  }

  async login(playerCode: string) {
    //log it
    console.log('Logging in with player code:', playerCode);
    const session = await lastValueFrom(this.api.startSession(playerCode));
    this.sessionId = session.sessionId;
    this.playerCode = session.playerCode;
    this._isLoggedIn = true;
    this.onLoginSubject.next(this.playerCode); // Emit playerCode on login
  }

  logout() {
    this.sessionId = undefined;
    this.playerCode = undefined;
    this._isLoggedIn = false;
    return this.stopPolling();
  }

  public async startPolling(): Promise<void> {
    const response = await this.updatePlaythrough(undefined, this.currentPlaythroughObjectives);

    if (!response.ref) {
      console.warn('Playthrough reference not found, retrying...');
      await waitForMillis(1000); // Wait before retrying
      return this.startPolling();
    }

    this.playthroughRef = response.ref;
    this.pollerId = setInterval(async () => {
      if (this.playthroughRef) {
        await this.updatePlaythrough(this.playthroughRef, this.currentPlaythroughObjectives);
      } else {
        console.warn('Playthrough reference is not set: going to stop polling.');
        await this.stopPolling();
      }
    }, this.POLLING_INTERVAL_MS);
  }

  public async stopPolling() {
    if (this.pollerId) {
      clearInterval(this.pollerId);
      this.pollerId = undefined;

      // final update
      if (this.playthroughRef) {
        await this.updatePlaythrough(this.playthroughRef, this.currentPlaythroughObjectives);
      }

      this.currentPlaythroughObjectives = [];
    }
  }

  // Updates the playthrough with the elapsed time since login
  private async updatePlaythrough(playthroughRef?: string, objectives?: Leerkr8Objective[]) {
    if (!this.startPollingTime || !this.startPollingDateString) {
      this.startPollingTime = Date.now();
      this.startPollingDateString = new Date().toISOString();
    }

    const elapsedTime = Math.floor((Date.now() - (this.startPollingTime as number)) / 1000);

    const playthroughRequest: Leerkr8PlaythroughRequest = {
      ref: playthroughRef ?? '',
      levelRef: '',
      startTime: this.startPollingDateString,
      endTime: new Date().toISOString(),
      score: 0,
      result: 'success',
      playTimeSeconds: elapsedTime,
      objectives,
    };

    if (!this.sessionId || !this.playerCode) {
      throw new Leerkr8Error('Not logged in', 'NOT_LOGGED_IN');
    }

    try {
      return await lastValueFrom(this.api.postPlaythrough(this.sessionId, this.playerCode, playthroughRequest));
    } catch (error) {
      console.error('Error while posting playthrough', error);
      throw error;
    }
  }
}
